/**
 * Renders the Verify root-cause sub-agent child harness run. Assistant prose
 * uses the WorkChat source-grounded citation pipeline, which the host renders.
 */

import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Loader2 } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import type { ChatItem, ToolUseItem } from '../../../../contracts/harness.types';

export interface VerifyChildTimelineProps {
  items: ChatItem[];
  isStreaming?: boolean;
  className?: string;
}

/**
 * What the app supplies: the source-grounded assistant prose, which belongs to
 * the evidence partition, as a render prop.
 */
export interface VerifyChildTimelineHost {
  renderAssistantText: (text: string, streaming: boolean) => ReactNode;
}

const SUPPRESSED_KINDS = new Set<ChatItem['kind']>([
  'thinking',
  'user_message',
  'turn_cancelled_marker',
  'turn_failed',
  'prompt_context_failed',
]);

const findLastRenderableIndex = (items: readonly ChatItem[]): number => {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const candidate = items[i];
    if (!candidate || SUPPRESSED_KINDS.has(candidate.kind)) continue;
    return i;
  }
  return -1;
};

export const VerifyChildTimeline = ({
  items,
  isStreaming = false,
  className,
  renderAssistantText,
}: VerifyChildTimelineProps & VerifyChildTimelineHost): JSX.Element => {
  const lastRenderableIndex = findLastRenderableIndex(items);
  const rendered = items.flatMap((item, index) => {
    if (SUPPRESSED_KINDS.has(item.kind)) return [];

    const active = isStreaming && index === lastRenderableIndex;

    if (item.kind === 'assistant_text') {
      if (!item.text) return [];
      return [
        <Fragment key={item.id}>{renderAssistantText(item.text, active)}</Fragment>,
      ];
    }

    if (item.kind === 'tool_use') {
      return [<UnknownToolPill key={item.id} name={item.name} active={active} />];
    }

    return [];
  });

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-5',
        !isStreaming && '[&_.animate-pulse]:animate-none [&_.animate-spin]:animate-none',
        className
      )}
      data-testid="verify-child-timeline"
    >
      {rendered}
    </div>
  );
};

const UnknownToolPill = ({
  name,
  active,
}: {
  name: ToolUseItem['name'];
  active: boolean;
}): JSX.Element => (
  <div className="flex items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs text-text-secondary">
    <Loader2
      className={cn('size-3.5 shrink-0 text-text-tertiary', active && 'animate-spin')}
    />
    <TypographyCaption className="truncate text-text-secondary">
      {name.replace(/_/g, ' ')}
    </TypographyCaption>
  </div>
);
